/**
 * HottrOrb — adopted verbatim from brand/icons/HottrOrb.tsx, the canonical
 * implementation that ships with HOTTR_Orb_Design_System_v2.0.md.
 *
 * Deliberately NOT reimplemented here. Three earlier hand-written attempts at
 * this component (flat disc, hollow neon ring, six-layer sphere) each drifted
 * from the spec in a different direction; this file is the authored reference,
 * so it is the source of truth and edits belong upstream in brand/icons/.
 *
 * The markup is unchanged, but the MATERIAL is v1.1 (HOTTR_Orb_Preview_v1_1.html):
 * a solid canonical-pink body with asymmetric lower-right depth, a masked conic
 * rim and a curved pink crescent — no gradient ramp, no white hotspot. That
 * correction lives entirely in hottr-orb.css; see the construction order at its
 * top. (Deliberately no literal hex in this comment: care's brand guard scans
 * every synced file, and a hex quoted in prose fails it just as a hard-coded
 * colour would.)
 *
 * Note what the product hooks actually do: `--orb-highlight-x/y` moves the
 * LIGHT SOURCE per product rather than swapping in a different icon. That is
 * how one Orb serves thirteen worlds — HOTTR.pro is lit from above like a
 * spotlight, HOTTR.care from softer and lower. Same object, different light.
 *
 * Requires kit/hottr-orb.css, imported below. That stylesheet owns every
 * @keyframes the Orb uses; tokens.css deliberately defines none of them.
 */
import { type CSSProperties, useId } from "react";
import "./hottr-orb.css";

export type OrbLevel = "core" | "living" | "cinematic";

export type OrbState =
  | "resting"
  | "interested"
  | "connected"
  | "activated"
  | "intense";

export type OrbProduct =
  | "app"
  | "tv"
  | "men"
  | "travel"
  | "brand"
  | "care"
  | "chat"
  | "me"
  | "media"
  | "music"
  | "news"
  | "pro"
  | "careers"
  | "passport"
  | "world";

export type HottrOrbProps = {
  level?: OrbLevel;
  state?: OrbState;
  product?: OrbProduct;
  size?: number | string;
  intensity?: number;
  decorative?: boolean;
  ariaLabel?: string;
  className?: string;
  reducedMotion?: boolean;
};

type OrbStyle = CSSProperties & {
  "--orb-size"?: string;
  "--orb-intensity"?: number;
};

const PARTICLES = [
  [8, 20, 1.6, 0.2],
  [15, 74, 1.1, 0.7],
  [22, 10, 1.2, 1.3],
  [28, 88, 1.8, 0.4],
  [38, 5, 1.0, 1.7],
  [46, 94, 1.4, 0.9],
  [58, 7, 1.3, 1.1],
  [64, 91, 1.7, 0.1],
  [76, 13, 1.1, 1.9],
  [82, 78, 1.5, 0.6],
  [91, 26, 1.2, 1.5],
  [94, 63, 1.6, 0.3],
] as const;

const CONNECTIONS = [
  [-58, -10, 52, -12],
  [54, -14, 82, -40],
  [-52, 20, -76, 42],
  [48, 24, 76, 48],
] as const;

function toCssSize(size: number | string): string {
  return typeof size === "number" ? `${size}px` : size;
}

export function HottrOrb({
  level = "living",
  state = "resting",
  product,
  size = 128,
  intensity = 1,
  decorative = true,
  ariaLabel = "HOTTR Orb",
  className = "",
  reducedMotion = false,
}: HottrOrbProps) {
  const id = useId().replace(/:/g, "");
  const clampedIntensity = Math.max(0.5, Math.min(1.5, intensity));

  const style: OrbStyle = {
    "--orb-size": toCssSize(size),
    "--orb-intensity": clampedIntensity,
  };

  const classes = [
    "hottr-orb",
    `hottr-orb--${level}`,
    `hottr-orb--${state}`,
    product ? `hottr-orb--product-${product}` : "",
    reducedMotion ? "hottr-orb--reduced-motion" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={classes}
      style={style}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : ariaLabel}
      data-orb-id={id}
    >
      <span className="hottr-orb__far-bloom" />
      <span className="hottr-orb__near-bloom" />

      {state === "connected" && (
        <span className="hottr-orb__connections" aria-hidden="true">
          {CONNECTIONS.map(([x1, y1, x2, y2], index) => (
            <span
              key={index}
              className="hottr-orb__connection"
              style={
                {
                  "--x1": `${x1}%`,
                  "--y1": `${y1}%`,
                  "--x2": `${x2}%`,
                  "--y2": `${y2}%`,
                  "--delay": `${index * 120}ms`,
                } as CSSProperties
              }
            />
          ))}
        </span>
      )}

      <span className="hottr-orb__shell">
        <span className="hottr-orb__body" />
        <span className="hottr-orb__depth" />
        <span className="hottr-orb__energy" />
        <span className="hottr-orb__highlight" />
        <span className="hottr-orb__rim" />
      </span>

      {(level === "cinematic" || state === "intense") && (
        <span className="hottr-orb__particles" aria-hidden="true">
          {PARTICLES.map(([x, y, scale, delay], index) => (
            <span
              key={index}
              className="hottr-orb__particle"
              style={
                {
                  "--x": `${x}%`,
                  "--y": `${y}%`,
                  "--particle-scale": scale,
                  "--particle-delay": `${delay}s`,
                } as CSSProperties
              }
            />
          ))}
        </span>
      )}

      {state === "activated" && (
        <span className="hottr-orb__activation-ring" aria-hidden="true" />
      )}
    </span>
  );
}

export default HottrOrb;
